/**
 * Which not-yet-shipped backend endpoint groups this build may call.
 *
 * The editor token covers 7 endpoints today (verified against
 * docs/api/storemandu-api-docs.json). The editor needs 12 more, requested in
 * TEMPLATE_EDITOR_API_CONTRACT_REQUEST.md. Each group sits behind a flag that
 * is OFF unless VITE_STUDIO_API_CAPABILITIES opts in, so the core edit loop
 * ships now and each group lights up the moment backend delivers it — with no
 * rework and no silent 404 noise in between.
 */
export type OptionalCapability =
  /** GET /template-editor/context — the five publish-preflight reads, consolidated. */
  | "context"
  /** GET /template-editor/categories* and /products* — CTA link pickers. */
  | "browse"
  /** POST /template-editor/media/upload-urls — every image upload. */
  | "media"
  /** store-templates revisions / restore / rollback — version history. */
  | "lifecycleExtras"
  /** POST /store-templates — adopt a template from the gallery. */
  | "adopt"
  /** store-templates carry-over preview/apply — the switch review screen. */
  | "carryOver"
  /** GET /template-editor/materials — Trikaya's materials picker. */
  | "materials";

const ALL: readonly OptionalCapability[] = [
  "context",
  "browse",
  "media",
  "lifecycleExtras",
  "adopt",
  "carryOver",
  "materials",
];

/** Split and validate the env allow-list. Unknown names are dropped, not thrown. */
export function parseCapabilities(raw: string | undefined): OptionalCapability[] {
  if (!raw) return [];
  const dropped: string[] = [];
  const known = raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => {
      if ((ALL as readonly string[]).includes(part)) return true;
      dropped.push(part);
      return false;
    }) as OptionalCapability[];

  if (dropped.length > 0 && !import.meta.env.PROD) {
    // A typo'd flag must not kill boot, but it must not be silent either —
    // the eventual CapabilityUnavailableError would misdiagnose it as a
    // backend gap.
    // eslint-disable-next-line no-console
    console.warn(`[studio] Unknown capability name(s) ignored: ${dropped.join(", ")}`);
  }

  return known;
}

function readFromEnv(): Set<OptionalCapability> {
  return new Set(
    parseCapabilities(
      import.meta.env.VITE_STUDIO_API_CAPABILITIES as string | undefined,
    ),
  );
}

let enabled = readFromEnv();

export function isCapabilityEnabled(capability: OptionalCapability): boolean {
  return enabled.has(capability);
}

/**
 * Demote for the rest of the session. Called when a gated route answers
 * 404/501 despite being allow-listed — an optimistic flag then degrades to
 * the same fail-fast refusal as a flag that was never set.
 */
export function disableCapability(capability: OptionalCapability): void {
  enabled.delete(capability);
}

/** Test seam: restore the env-derived set. */
export function resetCapabilities(): void {
  enabled = readFromEnv();
}

/** Test seam: force a capability on without touching the environment. */
export function enableCapabilityForTest(capability: OptionalCapability): void {
  enabled.add(capability);
}

/**
 * Which optional capability a request needs — or null when it is part of the
 * 7-endpoint surface the editor token already covers.
 *
 * Tolerant of every URL form a request can carry by the time it is inspected:
 * short call-site paths ("/categories"), rewritten paths
 * ("/merchant/template-editor/categories" — retries re-enter the request
 * interceptors), and absolute URLs. Query strings never participate.
 */
export function capabilityForRequest(
  method: string,
  url: string,
): OptionalCapability | null {
  let path = url.replace(/^https?:\/\/[^/]+/, "");
  path = path.replace(/^\/api\/v2(?=\/)/, "");
  path = path.replace(/^\/merchant\/template-editor(?=\/)/, "");
  if (!path.startsWith("/")) path = `/${path}`;
  path = path.split("?")[0];
  const m = method.toUpperCase();

  if (path === "/context") return "context";
  if (path === "/categories" || path.startsWith("/categories/")) return "browse";
  if (path === "/products" || path.startsWith("/products/")) return "browse";
  if (path === "/materials" || path.startsWith("/materials/")) return "materials";
  if (path === "/media/upload-urls") return "media";
  // Before the lifecycleExtras test: disjoint, but the file reads
  // most-specific-first, and an unmatched path returns null — which would let a
  // carry-over call reach the network with the flag off.
  if (/^\/store-templates\/[^/]+\/carry-over$/.test(path)) return "carryOver";
  if (/^\/store-templates\/[^/]+\/(revisions|restore|rollback)$/.test(path)) {
    return "lifecycleExtras";
  }
  if (m === "POST" && path === "/store-templates") return "adopt";
  return null;
}
